import type { TraderProfile as MesaTraderProfile } from '@/types/api/legacy-trader-profile'
import type { FullOrderBookResponse, Orders } from '@/types/api/order-book'
import type { QuoteType } from '@/types/api/quotes'
import type { AdvisorViewStatus } from '@/types/enums/advisor-view-status'
import type { OrderSide } from '@/types/enums/order'
import type { TraderViewStatus } from '@/types/enums/trader-view-status'

// =========================================
// Enriched Order Types
// =========================================

export enum RFQQuoteStatus {
  // Quote can be traded against
  Actionable = 'actionable',
  // Quote is expired
  Expired = 'expired',
  // Quote is not yet actionable (e.g., BIN RFQ and due time has not yet been reached)
  NotYetActionable = 'not_yet_actionable',
  // Quote is unsuccessful (e.g., quote response was rejected or canceled)
  Unsuccessful = 'unsuccessful',
  // Quote is not actionable at all
  NotActionable = 'not_actionable',
}

// Order book entries enriched with source information (LOB/RFQ)
//
// After hydration, the base FullOrderBookResponse fields (price, yield_to_maturity,
// yield_to_worst, spread_to_benchmark_bps) are overwritten with post-fee values from
// the HydratedQuote. The pre/post-fee fields below preserve both the original (pre-fee)
// and hydrated (post-fee) values explicitly. The post-fee fields duplicate the base
// fields for symmetry — consumers should prefer the named pre/post-fee fields.
export type EnrichedOrderBookResponse = FullOrderBookResponse & {
  source: QuoteSource
  preFeePrice?: number | null
  postFeePrice?: number | null
  preFeeYtm?: number | null
  postFeeYtm?: number | null
  preFeeYtw?: number | null
  postFeeYtw?: number | null
  preFeeSpreadBps?: number | null
  postFeeSpreadBps?: number | null
  side: QuoteType
  disabled?: boolean
  highlighted?: boolean
  // RFQ-specific identifiers
  rfqId?: string
  venueQuoteId?: string
  rfqQuoteStatus?: RFQQuoteStatus
}

export interface EnrichedOrders {
  bids: EnrichedOrderBookResponse[]
  asks: EnrichedOrderBookResponse[]
}

// =========================================
// Core Order Book Types
// =========================================

export enum ActivePanelType {
  Ask = 'ask',
  Bid = 'bid',
  Both = 'both',
}

export enum QuoteSide {
  Bid = 'bid',
  Ask = 'ask',
}

export enum QuoteSource {
  LOB = 'LOB',
  RFQ = 'RFQ',
}

// =========================================
// Data Types
// =========================================

// `number` was added because getDisplaySpreadValue (display-value-helpers.ts)
// can return preFeeSpreadBps (number) when pre-fee values are preferred. This is
// safe because order-book-tables.tsx wraps values in Number() before formatting.
export type SpreadDisplayValue =
  | { preFeeSpreadBps?: number | null; postFeeSpreadBps?: number | null }
  | string
  | number
  | null

export type PriceDisplayValue =
  | { preFeePrice?: number | null; postFeePrice?: number | null }
  | number
  | null

export type YieldDisplayValue =
  | { preFeeYtw?: number | null; postFeeYtw?: number | null }
  | number
  | null

// Input types for display value helpers (narrow types requiring only needed properties)
export interface SpreadQuoteInput {
  preFeeSpreadBps?: number | null
  postFeeSpreadBps?: number | null
  spread_to_benchmark_bps: string | null
}

export interface PriceQuoteInput {
  preFeePrice?: number | null
  postFeePrice?: number | null
  price: number | null
}

export interface YieldQuoteInput {
  preFeeYtw?: number | null
  postFeeYtw?: number | null
  yield_to_worst: number | null
}

export interface OrderBookRowItem {
  bidVenue: string | null
  bidMinNumBonds: number | null
  bidNumBonds: number | null
  bidMinBalanceRemaining: number | null
  bidMinIncrement: number | null
  bidYieldToWorst: YieldDisplayValue
  bidPrice: PriceDisplayValue
  bidSpreadToBenchmarkBps: SpreadDisplayValue
  bidSource?: QuoteSource | null
  bidDisabled?: boolean
  bidHighlighted?: boolean
  bidRfqQuoteStatus?: RFQQuoteStatus
  bidRfqId?: string | null
  bidVenueQuoteId?: string | null
  askMinNumBonds: number | null
  askNumBonds: number | null
  askMinBalanceRemaining: number | null
  askMinIncrement: number | null
  askYieldToWorst: YieldDisplayValue
  askPrice: PriceDisplayValue
  askVenue: string | null
  askSpreadToBenchmarkBps: SpreadDisplayValue
  askSource?: QuoteSource | null
  askDisabled?: boolean
  askHighlighted?: boolean
  askRfqQuoteStatus?: RFQQuoteStatus
  askRfqId?: string | null
  askVenueQuoteId?: string | null
  // Advisor concession offered by the desk on this ask, if any (bids never
  // carry one). Optional + nullable to match the other buildOrderBook-populated
  // fields above, so partial row literals need not specify it.
  askAdvisorConcession?: number | null
}

export interface OrderSummaryData {
  bidCount: number
  bidRange: string
  marketMid: string
  askRange: string
  askCount: number
  bidEnabledCount: number
  askEnabledCount: number
}

export interface EnrichedOrderBookRowItem extends OrderBookRowItem {
  disabled: boolean
  highlighted: boolean
}

// =========================================
// Component Props Types
// =========================================

export interface OrderBookProps {
  instrumentId: string
  riskGroupId?: string
  orders?: Orders
  minNumBonds?: number // Visually disable orders below this quantity
  maxNumBonds?: number // Visually disable orders above this quantity
  baseClassName?: string
  className?: string
  hasBorder?: boolean
  rows?: number
  collapsible?: boolean
  status?: TraderViewStatus | AdvisorViewStatus | undefined
  onRowAction?: (
    orderData: OrderBookRowItem,
    side: QuoteSide,
    traderProfile: MesaTraderProfile,
  ) => Promise<void>
  isButtonDisabled?: boolean
  showVenuesColumn?: boolean
  // Opt-in for trader archetype: keep the venue column visible even on the
  // combined (bid + ask) tab. Off by default so trading flows with limited
  // horizontal real-estate aren't forced to render the extra column.
  alwaysShowVenuesColumnForTrader?: boolean
  showSource?: boolean // Show source column (LOB or RFQ)
  orderSide?: OrderSide.Buy | OrderSide.Sell // Order side to determine default panel
  showActionButtonsOnAllRows?: boolean // Show action buttons on all rows when true, only on first valid row when false
  hideActionButtonsForNonApplicableTradeSide?: boolean // Hide action buttons for non-applicable trade side when true
  showPreAndPostFeeValues?: boolean // Show pre and post fee values when true
  titleText?: string // Show title text, aligned top left
  secondaryText?: string // Show secondary text, aligned top right
  isQuoteSnapshot?: boolean // Is order book showing snapshot of quotes from when order was executed?
  showControlPanel?: boolean // Show control panel for advanced filtering
  onCollapse?: () => void
}

export interface OrderBookTablesProps {
  rows?: number
  isCollapsed?: boolean
  activePanel: ActivePanelType
  orders: OrderBookRowItem[]
  bids: EnrichedOrderBookRowItem[]
  asks: EnrichedOrderBookRowItem[]
  onRowAction?: (
    orderData: OrderBookRowItem,
    side: QuoteSide,
    traderProfile: MesaTraderProfile,
  ) => Promise<void>
  isButtonDisabled?: boolean
  showVenuesColumn?: boolean
  showSource?: boolean
  showMbrAndIncrement?: boolean
  benchmarkIsin?: string | null
  orderSide?: OrderSide.Buy | OrderSide.Sell // Order side to determine button visibility
  showActionButtonsOnAllRows?: boolean // Show action buttons on all rows when true, only on first valid row when false
  hideActionButtonsForNonApplicableTradeSide?: boolean // Hide action buttons for non-applicable trade side when true
  showPreAndPostFeeValues?: boolean // Show pre and post fee values when true
  isQuoteSnapshot?: boolean // Is order book showing snapshot of quotes from when order was executed?
}

export interface OrderBookSummaryBannerProps {
  summaryData: OrderSummaryData
  activePanel: ActivePanelType
  selectSide: (side: ActivePanelType) => void
}

// =========================================
// Style Component Props
// =========================================

export interface OrderBookContainerProps {
  children: React.ReactNode
  hasBorder?: boolean
  className?: string
}

export interface OrderBookHeaderProps {
  isCollapsed?: boolean
  onToggleCollapse?: () => void
}

export interface OrderBookTableContainerProps {
  children: React.ReactNode
  showBoth: boolean
  rows?: number
  className?: string
  isCollapsed?: boolean
  showPreAndPostFeeValues?: boolean
}

export interface OrderBookCellProps {
  children: React.ReactNode
  className?: string
  hasBorder?: boolean
  height?: number
}

export interface HighlightProps {
  children: React.ReactNode
  className?: string
}

// Banner style types for consistent theme application
export interface BannerStyleMap {
  asksActive: string
  bidsActive: string
  neutral: string
}

export interface HeaderItem {
  label: string
  className?: string
}
